import { useBreadcrumb } from '@/hooks/useBreadcrumb';
import { useLocales } from '@/Locales';
import { recordTypeConfig } from '@/pages/patient/hooks/usePatientDetail';
import { globalFontSizeKeyAtom } from '@/store/theme';
import { BreadcrumbItem } from '@/types/breadcrumb';
import { Breadcrumb } from '@sinohealth/butterfly-ui-components';
import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import styles from './index.less';

interface BreadcrumbComponentProps {
  onChange: (height: number) => void;
}

const BreadcrumbComponent = ({ onChange }: BreadcrumbComponentProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const recordType = params.get('recordType') || '';
  const recordId = params.get('recordId') || '';
  const { t } = useLocales();
  const globalFontSizeKey = useRecoilValue(globalFontSizeKeyAtom);
  const breadcrumbRef = useRef<HTMLDivElement>(null);

  // 使用动态面包屑 hook
  const dynamicBreadcrumbs = useBreadcrumb();

  // 处理特殊情况的面包屑
  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    const pathname = location.pathname;

    // 患者详情新增病历记录特殊处理
    if (pathname === '/patient/patientDetailForm' && recordId) {
      return [
        { title: '首页', path: '/index' },
        { title: '患者病历', path: '/patient/list' },
        {
          title: '患者病历详情',
          path: '/patient/detail',
          back: true,
        },
        {
          title: !recordId ? '编辑记录' : recordTypeConfig[recordType]?.label,
          path: '/patient/patientDetailForm',
        },
      ];
    }

    return dynamicBreadcrumbs;
  }, [location.pathname, recordId, recordType, dynamicBreadcrumbs]);

  // 获取面包屑高度
  useEffect(() => {
    const updateHeight = () => {
      if (breadcrumbRef.current) {
        const height = breadcrumbRef.current.offsetHeight;
        onChange?.(height);
      }
    };

    updateHeight();

    // 使用 ResizeObserver 监听高度变化
    const resizeObserver = new ResizeObserver(() => {
      updateHeight();
    });

    if (breadcrumbRef.current) {
      resizeObserver.observe(breadcrumbRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [breadcrumbItems, globalFontSizeKey, onChange]);

  /**
   * 处理面包屑点击
   * @param index 点击的面包屑索引
   * @param item 面包屑项
   */
  const handleClick = (index: number, item: BreadcrumbItem) => {
    const total = breadcrumbItems.length;

    // 如果面包屑超过3级，中间项（非首页、非当前页）都使用返回
    // 例如：首页 > 联合病房MDT团队 > 团队详情 > 患者详情
    // 点击"联合病房MDT团队"或"团队详情"都应该返回
    if (total > 3 && index > 0 && index < total - 1) {
      // 计算需要返回的步数：从当前页到目标页的距离
      const stepsBack = total - 1 - index;
      navigate(-stepsBack);
      return;
    }

    // 如果配置了 back 属性，使用返回
    if (item.back) {
      navigate(-1);
      return;
    }

    // 否则直接跳转
    navigate(item.path);
  };

  if (location.pathname === '/index') return null;

  return breadcrumbItems.length > 0 ? (
    <div ref={breadcrumbRef} className={styles.breadcrumb}>
      <Breadcrumb>
        {breadcrumbItems.map((item, index) => (
          <Breadcrumb.Item key={item.path}>
            {index === breadcrumbItems.length - 1 ? (
              t(item.title)
            ) : (
              <a onClick={() => handleClick(index, item)}>{t(item.title)}</a>
            )}
          </Breadcrumb.Item>
        ))}
      </Breadcrumb>
    </div>
  ) : null;
};

export default BreadcrumbComponent;
